import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type User } from '@shared/types';
import { AuthError, authService } from '@features/authentication/services/authService';
import { analyticsService } from '@shared/services/analyticsService';

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  fieldErrors: Record<string, string[]> | null;

  // Auth methods
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUser: (updates: Partial<User>) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * useAuth hook - Access authentication context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    if (authService.isTokenExpired()) {
      authService.logout();
      return null;
    }
    const currentUser = authService.getCurrentUser();
    return currentUser ? (currentUser as User) : null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  // Listen for session expiry from API interceptors
  useEffect(() => {
    const handleSessionExpired = () => {
      analyticsService.trackSessionEnd();
      analyticsService.reset();
      authService.logout();
      setUser(null);
    };
    globalThis.addEventListener('auth:session-expired', handleSessionExpired);
    return () => globalThis.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    setFieldErrors(null);
    try {
      await authService.login(email, password);
      const currentUser = authService.getCurrentUser();
      setUser(currentUser as User);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'errors.loginFailed');
      if (err instanceof AuthError && err.fieldErrors) {
        setFieldErrors(err.fieldErrors);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    setFieldErrors(null);
    try {
      await authService.register(name, email, password);
      const currentUser = authService.getCurrentUser();
      setUser(currentUser as User);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'errors.registrationFailed');
      if (err instanceof AuthError && err.fieldErrors) {
        setFieldErrors(err.fieldErrors);
      }
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // End analytics session before clearing auth tokens
      analyticsService.trackSessionEnd();
      analyticsService.reset();
      authService.logout();
      setUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logout failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setFieldErrors(null);
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      // Sync localStorage so AuthContext restores correctly on refresh
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      error,
      fieldErrors,
      login,
      register,
      logout,
      clearError,
      updateUser,
    }),
    [user, isLoading, error, fieldErrors, login, register, logout, clearError, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
